// components/PurchasingIndividualPerformancePanel.jsx
import React, { useMemo } from 'react';
import { Box, Text, Loader, Alert, Tooltip } from '@mantine/core';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config';
import { getMetricsConfig } from './metricsConfig';

const PurchasingIndividualPerformancePanel = ({ selectedUser, filter }) => {
  const shouldFetchData = selectedUser && selectedUser.cCode && selectedUser.cCode.trim() !== '';

  const buildApiUrl = () => {
    if (!shouldFetchData) return null;

    let url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard?ccode=${selectedUser.cCode}&filter=${filter}`;

    // Add filter parameter if available
    if (filter && filter.purchasingScorecardOnly !== undefined) {
      const filterParam = filter.purchasingScorecardOnly ? 'IN' : 'OUT';
      url += `&filter=${filterParam}`;
    }

    return url;
  };




  const { data, isLoading, error } = useAuthenticatedData(buildApiUrl());

  // Map the status from the API to actual colors for background and border.
  const statusColorMapping = {
    green: { background: '#2A7040', border: '#1E5A33' },
    yellow: { background: '#A07D32', border: '#7C6025' },
    red: { background: '#944848', border: '#7A3636' },
    gray: { background: '#4F4F4F', border: '#3B3B3B' }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (isNaN(value)) return 'N/A';
    return `${Math.round(value)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const getStatusColor = (value, target, isHigherBetter = true, thresholdFactor = 0.8) => {
    if (value === null || value === undefined) return 'gray';

    if (isHigherBetter) {
      if (value >= target) return 'green';
      if (value >= target * thresholdFactor) return 'yellow';
      return 'red';
    } else {
      if (value <= target) return 'green';
      if (value <= target * (2 - thresholdFactor)) return 'yellow';
      return 'red';
    }
  };

  const getMarginPercentStatus = (value) => {
    if (value === null || value === undefined || isNaN(value)) return 'gray';

    if (value >= 20) return 'green';
    if (value >= 15) return 'yellow';
    return 'red';
  };

  const getInventoryHealthStatus = (value, ageBucket) => {
    const goals = {
      '0-90': 56,
      '91-180': 26,
      '181-365': 16,
      '366+': 2
    };

    const goal = goals[ageBucket];

    if (ageBucket === '0-90') {
      if (value >= goal) return 'green';
      if (value >= goal * 0.85) return 'yellow';
      return 'red';
    } else {
      if (value <= goal) return 'green';
      if (value <= goal * 1.15) return 'yellow';
      return 'red';
    }
  };

  const buildDetailUrl = (basePath, period) => {
    const filterParam = filter && filter.purchasingScorecardOnly !== undefined
      ? (filter.purchasingScorecardOnly ? 'IN' : 'OUT')
      : null;

    let url = `/supply-chain/purchasing/${selectedUser.cCode}/scorecard/${basePath}?period=${period}`;

    if (filterParam) {
      url += `&filter=${filterParam}`;
    }

    return url;
  };

  // Helper functions for handling clicks.
  const handleInventoryPlannedSpendClick = (period) => {
    window.open(buildDetailUrl('inventory-planned-spend-details', period), '_blank');
  };

  const handleInventoryLandedSpendClick = (period) => {
    window.open(buildDetailUrl('inventory-landed-spend-details', period), '_blank');
  };

  const handleInventoryRevenueClick = (period) => {
    window.open(buildDetailUrl('inventory-revenue-details', period), '_blank');
  };

  const handleInventoryHealthClick = (period) => {
    window.open(buildDetailUrl('inventory-health-details', period), '_blank');
  };

  const handleNewVendorPOClick = (period) => {
    window.open(buildDetailUrl('new-vendor-po-details', period), '_blank');
  };

  const handleNewVendorRFQClick = (period) => {
    window.open(buildDetailUrl('new-vendor-rfq-details', period), '_blank');
  };

  // The CompactMetricBox component with support for onClick.
  const CompactMetricBox = ({ value, format, status, onClick, tooltip }) => {
    const safeValue = value ?? 0;
    const hasData = safeValue !== null && !isNaN(safeValue);
    const { background, border } = statusColorMapping[status] || { background: '#4F4F4F', border: '#3B3B3B' };

    const formattedValue =
      format === 'currency'
        ? formatCurrency(hasData ? safeValue : 0)
        : format === 'percent'
          ? formatPercent(hasData ? safeValue : 0)
          : hasData
            ? format === 'number'
              ? formatNumber(safeValue)
              : safeValue.toFixed(1)
            : 'N/A';

    return (
      <Tooltip label={tooltip || ''} position="top" withArrow>
        <Box
          onClick={onClick}
          style={{
            backgroundColor: background,
            padding: 8,
            borderRadius: 4,
            border: `2px solid ${border}`,
            width: 120,
            height: 40,
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: onClick ? 'pointer' : 'default'
          }}
        >
          <Text size="sm" c="white" fw={600}>
            {formattedValue}
          </Text>
        </Box>
      </Tooltip>
    );
  };

  if (!selectedUser) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Text size="lg" c="dimmed">
          Please select a purchasing user to view scorecard data.
        </Text>
      </Box>
    );
  }

  if (isLoading)
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Loader />
      </Box>
    );

  if (error)
    return (
      <Alert color="red" title="Error">
        Error loading purchasing scorecard data: {error.message}
      </Alert>
    );

  if (!data)
    return <Alert color="blue" title="No Data">No purchasing scorecard data available</Alert>;

  const metrics = getMetricsConfig(data, getStatusColor, getMarginPercentStatus, getInventoryHealthStatus);

  return (
    <Box style={{ backgroundColor: 'var(--mantine-color-dark-8)', padding: 0, borderRadius: 8, width: '100%', position: 'relative', marginTop: 10 }}>
      {metrics.map((metric, metricIndex) => (
        <Box
          key={metric.title}
          style={{
                   marginBottom: '8px',
                        padding: '6px',
                        borderRadius: '4px',
                        border: '1px solid var(--mantine-color-gray-3)',
                        position: 'relative'
          }}
        >
          {/* Special handling for the first metric (Planned Inventory Spend) */}
          {metricIndex === 0 ? (
            <Box style={{ display: 'flex' }}>
              {/* Left side - Planned Inventory */}
              <Box style={{ width: '50%' }}>
                <Box style={{ display: 'flex', marginBottom: 8 }}>
                  <Box style={{ width: 150 }}>
                    <Text size="md" fw={500}>
                      {metric.title}
                    </Text>
                    {metric.goalDetails.map((detail, index) => (
                      <Text
                        key={index}
                        size="sm"
                        c="dimmed"
                        style={{ display: 'block', fontWeight: 400 }}
                      >
                        {detail}
                      </Text>
                    ))}
                  </Box>
                  <Box style={{ flex: 1 }}>
                    {metric.rows.map((row, rowIndex) => (
                      <Box
                        key={rowIndex}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '140px 120px 120px 120px',
                          gap: 8,
                          marginBottom: 8,
                          alignItems: 'center'
                        }}
                      >
                        <Text
                          c="dimmed"
                          style={{
                            fontWeight: 500,
                            fontSize: '0.85rem',
                            alignSelf: 'center',
                            textAlign: 'right',
                            paddingRight: 8
                          }}
                        >
                          {row.label}
                        </Text>
                        {row.boxes.map((box, index) => (
                          <CompactMetricBox
                            key={index}
                            value={box.value}
                            status={box.status}
                            format={box.format}
                            tooltip={box.tooltip}
                            onClick={
                              (metric.title === 'Planned Inventory Spend' && box.mode)
                                ? () => handleInventoryPlannedSpendClick(box.mode)
                                : undefined
                            }
                          />
                        ))}
                      </Box>
                    ))}
                    <Box
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '140px 120px 120px 120px',
                        gap: 8,
                        marginTop: 4
                      }}
                    >
                      <Box />
                      <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                        Month to Date
                      </Text>
                      <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                        Qtr to Date
                      </Text>
                      <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                        Year to Date
                      </Text>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Right side - Landed Inventory */}
              {metric.landed && (
                <Box style={{ width: '50%' }}>
                  <Box style={{ display: 'flex', marginBottom: 8 }}>
                    <Box style={{ width: 150 }}>
                      <Text size="md" fw={500}>
                        {metric.landed.title}
                      </Text>
                      {metric.landed.goalDetails.map((detail, index) => (
                        <Text
                          key={index}
                          size="sm"
                          c="dimmed"
                          style={{ display: 'block', fontWeight: 400 }}
                        >
                          {detail}
                        </Text>
                      ))}
                    </Box>
                    <Box style={{ flex: 1 }}>
                      {metric.landed.rows.map((row, rowIndex) => (
                        <Box
                          key={rowIndex}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '140px 120px 120px 120px',
                            gap: 8,
                            marginBottom: 8,
                            alignItems: 'center'
                          }}
                        >
                          <Text
                            c="dimmed"
                            style={{
                              fontWeight: 500,
                              fontSize: '0.85rem',
                              alignSelf: 'center',
                              textAlign: 'right',
                              paddingRight: 8
                            }}
                          >
                            {row.label}
                          </Text>
                          {row.boxes.map((box, index) => (
                            <CompactMetricBox
                              key={index}
                              value={box.value}
                              status={box.status}
                              format={box.format}
                              onClick={
                                (metric.landed.title === 'Landed Inventory Spend' && box.mode)
                                  ? () => handleInventoryLandedSpendClick(box.mode)
                                  : undefined
                              }
                            />
                          ))}
                        </Box>
                      ))}
                      <Box
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '140px 120px 120px 120px',
                          gap: 8,
                          marginTop: 4
                        }}
                      >
                        <Box />
                        <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                          Month to Date
                        </Text>
                        <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                          Qtr to Date
                        </Text>
                        <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                          Year to Date
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            // Normal rendering for other metrics (Revenue/Margin, Inventory Health, etc.)
            <Box style={{ display: 'flex', marginBottom: 8 }}>
              <Box style={{ width: 150 }}>
                <Text size="md" fw={500}>
                  {metric.title}
                </Text>
                {metric.goalDetails.map((detail, index) => (
                  <Text
                    key={index}
                    size="sm"
                    c="dimmed"
                    style={{ display: 'block', fontWeight: 400 }}
                  >
                    {detail}
                  </Text>
                ))}
              </Box>
              <Box style={{ flex: 1 }}>
                {metric.rows.map((row, rowIndex) => (
                  <Box
                    key={rowIndex}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 120px 120px 120px',
                      gap: 8,
                      marginBottom: 8,
                      alignItems: 'center'
                    }}
                  >
                    <Text
                      c="dimmed"
                      style={{
                        fontWeight: 500,
                        fontSize: '0.85rem',
                        alignSelf: 'center',
                        textAlign: 'right',
                        paddingRight: 8
                      }}
                    >
                      {row.label}
                    </Text>
                    {row.boxes.map((box, index) => (
                      <CompactMetricBox
                        key={index}
                        value={box.value}
                        status={box.status}
                        format={box.format}
                        onClick={
                          (metric.title === 'Revenue/Margin' && box.mode)
                            ? () => handleInventoryRevenueClick(box.mode)
                            : (metric.title === 'Inventory Health')
                              ? () => handleInventoryHealthClick('current')
                              : (metric.title === 'New Vendor Base Growth' && row.label === '# of PO Actual' && box.mode)
                                ? () => handleNewVendorPOClick(box.mode)
                                : (metric.title === 'New Vendor Base Growth' && row.label === '# of RFQs' && box.mode)
                                  ? () => handleNewVendorRFQClick(box.mode)
                                  : undefined
                        }
                      />
                    ))}
                  </Box>
                ))}
                {metric.rows && metric.rows.length > 0 && metric.rows[0].boxes && metric.rows[0].boxes.length > 1 && (
                  <Box
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '140px 120px 120px 120px',
                      gap: 8,
                      marginTop: 4
                    }}
                  >
                    <Box />
                    <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                      Month to Date
                    </Text>
                    <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                      Qtr to Date
                    </Text>
                    <Text size="sm" c="dimmed" style={{ textAlign: 'center' }}>
                      Year to Date
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {metric.title === 'New Vendor Base Growth' && (
            <Box
              style={{
                position: 'absolute',
                right: 16,
                bottom: 16,
                borderRadius: 4,
                padding: 8,
                width: 80,
                textAlign: 'center'
              }}
            >
              <Text c="dimmed" size="xs">
                Direct Source
              </Text>
              <Text c="white" fw={700} size="lg">
                {metric.directSource || 'TBD'}
              </Text>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default PurchasingIndividualPerformancePanel;